import { create } from "zustand"
import { subscriptionsService } from "@/features/subscriptions/api"
import { SubscriptionPolicy } from "@/features/subscriptions/domain/subscription-policy"
import { env } from "@/config/env"
import { DEV_BYPASS_AUTH, DEV_BYPASS_SUBSCRIPTION } from "@/config/dev-bypass"
import type { UserSubscription } from "@/features/subscriptions/types"

export type VerificationStatus = "idle" | "verifying" | "verified" | "error"

/**
 * Local-access flag for the demo flow when no subscriptions backend is configured.
 * IMPORTANT: this is ONLY honored when the backend is not configured. In production
 * (backend configured) access is decided exclusively by the server response, so a
 * tampered flag here cannot unlock the app.
 */
const LOCAL_GRANT_KEY = "bp-local-access"

const policy = new SubscriptionPolicy({ bundle: env.VITE_SUBSCRIPTION_BUNDLE })

function readLocalGrant(): boolean {
  try {
    return sessionStorage.getItem(LOCAL_GRANT_KEY) === "true"
  } catch {
    return false
  }
}

interface SubscriptionState {
  status: VerificationStatus
  hasAccess: boolean
  isCanceled: boolean
  subscription: UserSubscription | null
  /** Raw subscriptions returned by backend, used for feature-level purchase flags. */
  subscriptions: UserSubscription[]
  error: string | null
  /** Verify access against the backend (or local grant when backend is absent). */
  verify: (uid: string) => Promise<void>
  /** Re-fetch subscriptions without unmounting the webapp (post-purchase refresh). */
  refreshSubscriptions: (uid: string) => Promise<void>
  /** Demo/no-backend fallback used by the post-purchase flow. */
  grantLocalAccess: () => void
  /**
   * Cancel a subscription (the active one by default). Access remains until the
   * period ends. Pass the subscription explicitly when the caller already has it
   * (e.g. the My Plan screen) so we cancel exactly what the user is looking at.
   */
  cancel: (subscription?: UserSubscription | null) => Promise<{ ok: boolean; error?: string }>
  reset: () => void
}

export const useSubscriptionStore = create<SubscriptionState>((set) => ({
  status: "idle",
  hasAccess: false,
  isCanceled: false,
  subscription: null,
  subscriptions: [],
  error: null,

  verify: async (uid) => {
    // DEV-only: grant access with a fake subscription, skip the backend check.
    // Dead-code-eliminated from production builds (see src/config/dev-bypass.ts).
    if (DEV_BYPASS_AUTH) {
      set({
        status: "verified",
        hasAccess: true,
        isCanceled: false,
        subscription: DEV_BYPASS_SUBSCRIPTION,
        subscriptions: [DEV_BYPASS_SUBSCRIPTION],
        error: null,
      })
      return
    }

    if (!subscriptionsService.isConfigured) {
      set({
        status: "verified",
        hasAccess: readLocalGrant(),
        subscription: null,
        subscriptions: [],
        isCanceled: false,
        error: null,
      })
      return
    }

    set({ status: "verifying", error: null })

    try {
      const subscriptions = await subscriptionsService.getUserSubscriptions(uid)
      const verdict = policy.evaluate(subscriptions)

      set({
        status: "verified",
        hasAccess: verdict.hasAccess,
        isCanceled: verdict.isCanceled,
        subscription: verdict.subscription,
        subscriptions,
        error: null,
      })
    } catch (err) {
      const message = err instanceof Error ? err.message : "Subscription check failed"
      set({
        status: "error",
        hasAccess: false,
        isCanceled: false,
        subscription: null,
        subscriptions: [],
        error: message,
      })
    }
  },

  refreshSubscriptions: async (uid) => {
    if (!subscriptionsService.isConfigured) {
      return
    }

    try {
      const subscriptions = await subscriptionsService.getUserSubscriptions(uid)
      const verdict = policy.evaluate(subscriptions)

      set((state) => ({
        status: state.status === "idle" ? "verified" : state.status,
        hasAccess: verdict.hasAccess,
        isCanceled: verdict.isCanceled,
        subscription: verdict.subscription,
        subscriptions,
        error: null,
      }))
    } catch (err) {
      const message = err instanceof Error ? err.message : "Subscription refresh failed"
      set({ error: message })
    }
  },

  grantLocalAccess: () => {
    try {
      sessionStorage.setItem(LOCAL_GRANT_KEY, "true")
    } catch {
      /* ignore */
    }
    if (!subscriptionsService.isConfigured) {
      set({ status: "verified", hasAccess: true })
    }
  },

  cancel: async (target) => {
    // DEV-only: reflect cancellation locally, never hit the real /api/cancel.
    if (DEV_BYPASS_AUTH) {
      set({ isCanceled: true })
      return { ok: true }
    }

    const subscription = target ?? useSubscriptionStore.getState().subscription

    // Demo / no-backend: just reflect the canceled state locally.
    if (!subscriptionsService.isConfigured || !subscription) {
      set({ isCanceled: true })
      return { ok: true }
    }

    const res = await subscriptionsService.cancelSubscription(subscription.id)
    if (!res.success) {
      return { ok: false, error: res.error }
    }

    set({ isCanceled: true })
    return { ok: true }
  },

  reset: () => {
    try {
      sessionStorage.removeItem(LOCAL_GRANT_KEY)
    } catch {
      /* ignore */
    }
    set({
      status: "idle",
      hasAccess: false,
      isCanceled: false,
      subscription: null,
      subscriptions: [],
      error: null,
    })
  },
}))
